import { F0Button, F0Text } from '@factorialco/f0-react'
import { Feed } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { BenchmarkMatchResult } from 'modules/job_catalog/benchmark/types'

/**
 * Approximate months since a survey year. The benchmark API only exposes the
 * survey `year` (no month/day), so this assumes the data was collected at the
 * start of that year. The result is therefore a rough estimate — e.g. a 2025
 * survey queried in January 2026 would show "12 months ago".
 *
 * Returns `null` when the survey year is current (0 months elapsed) so the
 * caller can omit the "last updated" label for recent data.
 */
const getMonthsSinceYear = (year: number | null | undefined): number | null => {
  if (year == null) return null
  const now = new Date()
  const months = (now.getFullYear() - year) * 12 + now.getMonth()
  if (months <= 0) return null
  return months
}
interface SalaryDisplay {
  amount: string
  periodicity: string
}

type BodyStatus = 'hasData' | 'noInfo' | 'suppressed'

interface Props {
  status: BodyStatus
  benchmark: BenchmarkMatchResult
  levelName: string
  roleName?: string
  workplaceName?: string
  salaryDisplay: SalaryDisplay
}

const SalaryPopoverBody = ({
  status,
  benchmark,
  levelName,
  roleName,
  workplaceName,
  salaryDisplay,
}: Props) => {
  if (status === 'noInfo' || status === 'suppressed') {
    const description =
      status === 'noInfo'
        ? i18n.t('job_catalog.benchmark.states.no_data.description')
        : i18n.t('job_catalog.benchmark.states.suppressed.description')

    return (
      // eslint-disable-next-line factorial/no-raw-html -- flex column layout with exact spacing not achievable via F0Box
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          background: 'white',
          padding: 8,
          borderRadius: 16,
        }}
      >
        {/* eslint-disable-next-line factorial/no-raw-html -- flex row for icon + text alignment not achievable via F0Box */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
          {/* eslint-disable-next-line factorial/no-raw-html -- wrapper div for icon sizing */}
          <div>
            <Feed color="hsl(var(--neutral-solid-40))" width={32} height={32} />
          </div>
          {/* eslint-disable-next-line factorial/no-raw-html -- wrapper div for text */}
          <div>
            <F0Text variant="description" content={description} />
          </div>
        </div>
      </div>
    )
  }

  return (
    // eslint-disable-next-line factorial/no-raw-html -- exact border, padding, and box-sizing not achievable via F0Box tokens
    <div
      style={{
        boxSizing: 'border-box',
        width: 392,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 8,
        padding: '8px 16px 12px',
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'hsl(var(--neutral-10))',
        background: 'white',
      }}
    >
      {/* eslint-disable-next-line factorial/no-raw-html -- flex column for salary label + value grouping */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <F0Text
          variant="description"
          content={i18n.t('job_catalog.benchmark.company_salary_range')}
        />

        {/* eslint-disable-next-line factorial/no-raw-html -- baseline alignment for amount + periodicity not achievable via F0Box */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 4 }}>
          {/* eslint-disable-next-line factorial/no-raw-html -- h3 with custom font-size/weight/letter-spacing not available via F0Text */}
          <h3
            style={{
              margin: 0,
              fontFamily: 'Inter',
              fontWeight: 500,
              fontSize: 22,
              lineHeight: '125%',
              letterSpacing: '0',
              color: 'hsl(var(--neutral-100))',
            }}
          >
            {salaryDisplay.amount}
          </h3>
          {/* eslint-disable-next-line factorial/no-raw-html -- span with custom font styling not available via F0Text */}
          <span
            style={{
              color: 'hsl(var(--neutral-100))',
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: '145%',
              letterSpacing: '0',
            }}
          >
            {salaryDisplay.periodicity}
          </span>
        </div>

        <F0Text
          variant="description"
          content={
            roleName
              ? workplaceName
                ? i18n.t('job_catalog.benchmark.company_salary_for_role_in_workplace', {
                    roleName,
                    levelName,
                    workplaceName,
                  })
                : i18n.t('job_catalog.benchmark.company_salary_for_role', { roleName, levelName })
              : i18n.t('job_catalog.benchmark.company_salary_for_level', { levelName })
          }
        />
      </div>

      {/* eslint-disable-next-line factorial/no-raw-html -- space-between row for metadata + button not achievable via F0Box */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        {(benchmark.orgCount != null || benchmark.year != null) && (
          // eslint-disable-next-line factorial/no-raw-html -- flex: 1 + minWidth: 0 for text truncation not achievable via F0Box
          <div style={{ flex: 1, minWidth: 0 }}>
            <F0Text
              variant="small"
              content={[
                benchmark.orgCount != null
                  ? i18n.tp('job_catalog.benchmark.based_on_data', {
                      count: benchmark.orgCount,
                    })
                  : null,
                (() => {
                  const months = getMonthsSinceYear(benchmark.year)
                  if (months == null) return null
                  return i18n.tp('job_catalog.benchmark.last_updated', { count: months })
                })(),
              ]
                .filter(Boolean)
                .join(' ')}
            />
          </div>
        )}
        {/* eslint-disable-next-line factorial/no-raw-html -- exact flex-shrink: 0 + fixed width for button not achievable via F0Box */}
        <div style={{ width: 102, minWidth: 102, flex: '0 0 102px', flexShrink: 0 }}>
          <F0Button
            label={String(i18n.t('job_catalog.benchmark.view_report'))}
            variant="neutral"
            size="sm"
            disabled
          />
        </div>
      </div>
    </div>
  )
}

export default SalaryPopoverBody
